import { useQuery } from '@tanstack/react-query'
import { DATA_CLASS } from './dataClasses'
import { getUserPermissionMap } from '@appic/lib/api/iam'
import { hasPermission, hasAppAccess, getGrantedFeatures } from '@appic/data'
import type { PermissionMap, PermissionAction } from '@appic/data'
import { useCurrentUser } from '@appic/lib/auth/UserContext'

/**
 * Fetches the resolved permission map for the current user.
 * Calls get_user_permission_map('me') and caches with TanStack Query.
 *
 * Returns utility functions that delegate to the iamTypes helpers.
 */
export function usePermissionMap() {
  const { email } = useCurrentUser()

  const query = useQuery<PermissionMap>({
    queryKey: ['iam-permission-map', 'me'],
    queryFn: () => getUserPermissionMap('me'),
    enabled: !!email,
    staleTime: DATA_CLASS.CONFIG,
    retry: 1,
    meta: { skipErrorToast: true },
  })

  const map = query.data ?? []

  return {
    permissionMap: map,
    isLoading: query.isLoading,
    error: query.error,

    /** Check if user has a specific action on an app (optionally feature). */
    check: (appId: string, action: PermissionAction = 'read', featureId?: string | null) =>
      hasPermission(map, appId, action, featureId),

    /** Check if user has any access to an app. */
    hasApp: (appId: string) => hasAppAccess(map, appId),

    /** Get all feature IDs the user can access for an app. */
    grantedFeatures: (appId: string, action: PermissionAction = 'read') =>
      getGrantedFeatures(map, appId, action),
  }
}
